/**
 * Fullscreen, centered version of Maya — intended for the WP /questionnaire page.
 * Public route: /questionnaire (no auth). Reuses the same backend chat session as
 * the embedded widget, but wrapped in an editorial, hero-style layout.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Check, Loader2, Compass, Sparkles, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = [
  { key: "countries", label: "Destination", check: (b) => Array.isArray(b?.countries) && b.countries.length > 0 },
  { key: "travelers", label: "Travelers", check: (b) => b?.adults != null },
  { key: "when", label: "Dates", check: (b) => !!b?.when },
  { key: "from_where", label: "Origin", check: (b) => !!b?.from_where },
  { key: "interests", label: "Style", check: (b) => Array.isArray(b?.interests) && b.interests.length > 0 },
  { key: "budget", label: "Budget", check: (b) => !!b?.budget_per_person },
  { key: "contact", label: "Contact", check: (b) => !!b?.email },
];

export default function QuestionnairePage() {
  const [sessionId, setSessionId] = useState(null);
  const [mayaAvatar, setMayaAvatar] = useState("");
  const [messages, setMessages] = useState([]);
  const [brief, setBrief] = useState({});
  const [complete, setComplete] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(true);
  const [quickReplies, setQuickReplies] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Hide the Emergent preview badge inside this public page
    const css = document.createElement("style");
    css.textContent = `#emergent-badge, a[href*="emergent.sh"], a[href*="emergent-badge"] { display: none !important; visibility: hidden !important; }`;
    document.head.appendChild(css);
    const obs = new MutationObserver(() => {
      const b = document.getElementById("emergent-badge");
      if (b) b.remove();
      document.querySelectorAll('a[href*="emergent.sh"]').forEach((el) => el.remove());
    });
    obs.observe(document.body, { childList: true, subtree: true });

    axios.get(`${API}/widget/config`).then((r) => {
      setMayaAvatar(r.data.maya_avatar_url || "");
    }).catch(() => {});

    (async () => {
      try {
        const qs = new URLSearchParams(window.location.search);
        const payload = {
          page_url: qs.get("page_url") || window.location.href,
          page_title: qs.get("page_title") || "Questionnaire — Espíritu Travel",
          page_referrer: qs.get("page_referrer") || document.referrer || null,
        };
        const r = await axios.post(`${API}/public/chat/start`, payload);
        setSessionId(r.data.session_id);
        setMessages([{ role: "assistant", content: r.data.reply }]);
        if (r.data.quick_replies?.length) setQuickReplies(r.data.quick_replies);
      } catch {
        setMessages([{ role: "assistant", content: "Sorry, there was a problem starting the chat. Please refresh the page or email us at hello@espiritutravel.com." }]);
      } finally {
        setStarting(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, sending]);

  const send = async (textOverride) => {
    const text = (typeof textOverride === "string" ? textOverride : input).trim();
    if (!text || !sessionId || sending) return;
    if (typeof textOverride !== "string") setInput("");
    setQuickReplies([]);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/chat/${sessionId}/message`, { message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
      setBrief(r.data.brief || {});
      setComplete(!!r.data.complete);
      setLeadId(r.data.lead_id);
      if (r.data.quick_replies?.length) setQuickReplies(r.data.quick_replies);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, something went wrong. Could you repeat your message?" }]);
    } finally {
      setSending(false);
    }
  };

  const completedSteps = STEP_LABELS.filter((s) => s.check(brief)).length;
  const progress = (completedSteps / STEP_LABELS.length) * 100;

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-8 sm:py-12"
      style={{
        background:
          "linear-gradient(135deg, #0A1128 0%, #1a2547 50%, #0A1128 100%)",
      }}
      data-testid="questionnaire-page"
    >
      {/* Decorative grain / glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #D95D39 0%, transparent 40%), radial-gradient(circle at 80% 70%, #D95D39 0%, transparent 50%)",
        }}
      />

      <div className="relative w-full max-w-2xl">
        {/* Top mini-brand bar */}
        <div className="flex items-center justify-center gap-2 mb-6 text-white/70">
          <Globe className="w-3.5 h-3.5 text-[#D95D39]" />
          <span className="text-[10px] uppercase tracking-[0.35em] font-medium">
            Espíritu Travel · Custom Journeys
          </span>
        </div>

        {/* Editorial title */}
        <div className="text-center mb-6 sm:mb-8">
          <h1
            className="text-4xl sm:text-5xl text-white leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontWeight: 600 }}
          >
            Start your <span className="text-[#D95D39]">journey</span>
          </h1>
          <p className="mt-3 text-white/65 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
            Chat with Maya — our virtual assistant — and we'll match you with a
            local specialist who designs trips like yours every day.
          </p>
        </div>

        {/* Card */}
        <div
          className="bg-white border border-[#E2DCD0] rounded-sm overflow-hidden flex flex-col"
          style={{
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.5), 0 10px 30px -10px rgba(217,93,57,0.15)",
            height: "min(78vh, 720px)",
          }}
        >
          {/* Header */}
          <header className="px-5 py-4 sm:px-6 sm:py-4 border-b border-[#E2DCD0] flex items-center justify-between gap-3 flex-shrink-0 bg-white">
            <div className="flex items-center gap-3 min-w-0">
              {mayaAvatar ? (
                <img
                  src={mayaAvatar}
                  alt="Maya"
                  className="w-11 h-11 rounded-sm object-cover shrink-0 border-2 border-[#D95D39]"
                />
              ) : (
                <div className="w-11 h-11 rounded-sm bg-[#0A1128] flex items-center justify-center shrink-0">
                  <Compass className="w-5 h-5 text-[#D95D39]" strokeWidth={1.5} />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-[0.25em] text-[#8A8F98]">
                  Travel Assistant
                </p>
                <h2
                  className="text-lg sm:text-xl text-[#0A1128] leading-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Maya
                </h2>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] shrink-0">
              <Sparkles className="w-3 h-3 text-[#D95D39]" />
              <span>Custom design</span>
            </div>
          </header>

          {/* Progress */}
          <div className="px-5 sm:px-6 pt-3 pb-2 flex-shrink-0 border-b border-[#F0EBE2]">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-2">
              <span data-testid="questionnaire-step-counter">
                Step {Math.min(completedSteps + 1, STEP_LABELS.length)} of {STEP_LABELS.length}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1 bg-[#E2DCD0] rounded-sm overflow-hidden">
              <div
                className="h-full bg-[#D95D39] transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Chat area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-3 min-h-0 bg-[#F9F8F6]"
            data-testid="questionnaire-messages"
          >
            {starting && (
              <div className="text-center text-[#8A8F98] text-sm py-10">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Starting conversation...
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                {m.role === "assistant" ? (
                  mayaAvatar ? (
                    <img src={mayaAvatar} alt="Maya" className="w-9 h-9 rounded-sm object-cover shrink-0 border-2 border-[#D95D39]" />
                  ) : (
                    <div className="w-9 h-9 rounded-sm bg-[#0A1128] flex items-center justify-center shrink-0">
                      <Compass className="w-4 h-4 text-white" strokeWidth={1.5} />
                    </div>
                  )
                ) : (
                  <div className="w-9 h-9 rounded-sm bg-[#D95D39] flex items-center justify-center shrink-0">
                    <span className="text-white text-xs font-medium">You</span>
                  </div>
                )}
                <div
                  className={`max-w-[78%] px-4 py-3 rounded-sm text-[15px] whitespace-pre-wrap leading-relaxed font-medium ${
                    m.role === "user"
                      ? "bg-[#0A1128] text-white"
                      : "bg-white border border-[#E2DCD0] text-[#0A1128]"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                {mayaAvatar ? (
                  <img src={mayaAvatar} alt="Maya" className="w-9 h-9 rounded-sm object-cover border-2 border-[#D95D39]" />
                ) : (
                  <div className="w-9 h-9 rounded-sm bg-[#0A1128] flex items-center justify-center">
                    <Compass className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="px-4 py-3 bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm text-sm text-[#8A8F98] italic">
                  Maya is typing<span className="inline-block w-3">...</span>
                </div>
              </div>
            )}
            {complete && leadId && (
              <div className="mx-auto max-w-md p-5 bg-[#2D6A4F]/5 border border-[#2D6A4F] rounded-sm text-center">
                <Check className="w-6 h-6 mx-auto text-[#2D6A4F] mb-2" />
                <p className="font-semibold text-[#0A1128]">Got it — we'll be in touch!</p>
                <p className="text-xs text-[#1A1F2B] mt-1 leading-relaxed">
                  Within <strong>24 business hours</strong> a local travel designer will reach out by phone or to the email you shared, to refine the details of the ideal trip we'll be designing for you.
                </p>
              </div>
            )}
          </div>

          {/* Quick replies */}
          {quickReplies.length > 0 && !sending && !complete && (
            <div className="px-3 pt-2 pb-1 flex flex-wrap gap-2 bg-white flex-shrink-0 border-t border-[#E2DCD0]">
              {quickReplies.map((q, i) => (
                <button
                  key={i}
                  onClick={() => send(q)}
                  data-testid={`questionnaire-quick-${i}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#F2EFE6] hover:bg-[#D95D39] hover:text-white border border-[#E2DCD0] text-[#0A1128] transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div
            className={`p-3 flex gap-2 bg-white flex-shrink-0 ${
              quickReplies.length > 0 && !sending && !complete ? "" : "border-t border-[#E2DCD0]"
            }`}
          >
            <input
              data-testid="questionnaire-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder={complete ? "Conversation complete" : "Type your reply..."}
              disabled={!sessionId || sending || complete}
              className="flex-1 px-3 py-2.5 bg-white border border-[#E2DCD0] rounded-sm text-[15px] font-medium text-[#0A1128] focus:outline-none focus:border-[#0A1128] disabled:opacity-50"
            />
            <button
              data-testid="questionnaire-send"
              onClick={() => send()}
              disabled={!sessionId || sending || !input.trim() || complete}
              className="px-4 bg-[#D95D39] hover:bg-[#c54e2e] disabled:bg-[#E2DCD0] disabled:cursor-not-allowed text-white rounded-sm transition-colors flex items-center justify-center"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/40 text-[10px] uppercase tracking-[0.3em] mt-6">
          Spain · Portugal · Italy · Morocco
        </p>
      </div>
    </div>
  );
}
